import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';

// Lazy-load below-the-fold sections to reduce initial JS bundle
const ServiceOverview = lazy(() => import('./components/ServiceOverview'));
const TrustBar = lazy(() => import('./components/TrustBar'));
const FeedbackSection = lazy(() => import('./components/FeedbackSection'));
const Footer = lazy(() => import('./components/Footer'));
const ContactBooking = lazy(() => import('./components/ContactBooking'));

function SectionSkeleton() {
  return <div className="min-h-screen bg-[#F0EDE6]" />;
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [selectedService, setSelectedService] = useState<string | undefined>(undefined);
  const [currentSection, setCurrentSection] = useState(0);
  const [navHeight, setNavHeight] = useState(72);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);

  const sections = ['hero', 'how-it-works', 'services', 'trust-bar', 'feedback-section', 'footer-section'];
  const totalSections = sections.length;

  const currentLanguage = i18n.language;

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'de' ? 'en' : 'de';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    const updateNavHeight = () => {
      if (navRef.current) {
        setNavHeight(navRef.current.offsetHeight);
      }
    };
    updateNavHeight();
    window.addEventListener('resize', updateNavHeight);
    return () => window.removeEventListener('resize', updateNavHeight);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://ski-service-kurier.ch';

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": `${siteUrl}/#business`,
          "name": "Ski-Service Kurier Klosters",
          "url": siteUrl,
          "description": "Professioneller Ski-Service Kurier mit Abholung am Abend (17:30) und Rückgabe am Morgen (07:30). Wachsen, Schleifen, Reparatur in Davos, Klosters und Arosa.",
          "telephone": "+41 XX XXX XX XX",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Klosters",
            "addressRegion": "Graubünden",
            "addressCountry": "CH"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 46.8771,
            "longitude": 9.8783
          },
          "areaServed": [
            { "@type": "City", "name": "Klosters" },
            { "@type": "City", "name": "Davos" },
            { "@type": "City", "name": "Arosa" }
          ],
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
              "opens": "07:30",
              "closes": "17:30"
            }
          ],
          "priceRange": "CHF 25–65",
          "currenciesAccepted": "CHF",
          "paymentAccepted": "Cash, Credit Card",
          "sameAs": []
        },
        {
          "@type": "Service",
          "@id": `${siteUrl}/#service`,
          "name": "Ski-Service Kurier",
          "provider": { "@id": `${siteUrl}/#business` },
          "serviceType": "Ski Maintenance & Repair",
          "description": "Abholung am Abend, professionelles Wachsen, Schleifen und Reparieren, Rückgabe am nächsten Morgen um 07:30 Uhr.",
          "areaServed": [
            { "@type": "City", "name": "Klosters" },
            { "@type": "City", "name": "Davos" }
          ],
          "offers": [
            {
              "@type": "Offer",
              "name": "Kleines Service-Paket",
              "priceCurrency": "CHF",
              "price": "60.00"
            },
            {
              "@type": "Offer",
              "name": "Grosses Service-Paket",
              "priceCurrency": "CHF",
              "price": "80.00"
            }
          ]
        },
        {
          "@type": "WebPage",
          "@id": `${siteUrl}/#webpage`,
          "url": siteUrl,
          "name": "Klosters Ski-Service Kurier | Bestellen bis 17:00 • Rückgabe 07:30",
          "description": "Professioneller Ski-Service Kurier mit Abholung am Abend und Rückgabe am Morgen. Perfekt präparierte Ski über Nacht in Davos und Klosters.",
          "inLanguage": "de-CH",
          "isPartOf": { "@id": `${siteUrl}/#website` }
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "Ski-Service Kurier",
          "inLanguage": "de-CH"
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById('schema-home');
      if (existing) existing.remove();
    };
  }, []);

  const scrollToSection = (id: string) => {
    const container = scrollContainerRef.current;
    const element = document.getElementById(id);
    if (container && element) {
      const offsetTop = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      container.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const scrollToIndex = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const target = index * window.innerHeight;
    container.scrollTo({ top: target, behavior: 'smooth' });
  };

  const handleMobileNavClick = (id: string) => {
    setMobileMenuOpen(false);
    scrollToSection(id);
  };

  const openBooking = () => {
    setSelectedService(undefined);
    setShowBooking(true);
  };

  const openBookingWithService = (service: string) => {
    setSelectedService(service);
    setShowBooking(true);
  };

  return (
    <div className="min-h-screen bg-[#F0EDE6]">
      {/* Navigation */}
      <nav
        ref={navRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-1.5 sm:py-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0 flex-shrink">
              <img
                src="https://static.readdy.ai/image/bd37f9b321d8d487bb2df5b36d703d09/5bc315f20d8b71200f0abd862b82a739.png"
                alt="Ski Service Kurier Logo"
                width="72"
                height="72"
                fetchPriority="high"
                loading="eager"
                decoding="async"
                className="h-12 xs:h-14 md:h-[72px] w-auto object-contain flex-shrink-0"
              />
              <div className="flex flex-col leading-tight min-w-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/37a9eaec-a4dc-46dd-a3bb-52522c41453b.png"
                  alt="Klosters"
                  width="200"
                  height="28"
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  className={`h-4 xs:h-5 md:h-7 w-auto object-contain transition-all ${isScrolled ? 'brightness-0' : 'brightness-0 invert'}`}
                />
                <span className={`text-[8px] xs:text-[9px] md:text-[11px] font-semibold tracking-widest uppercase transition-colors ${isScrolled ? 'text-gray-500' : 'text-white/80'}`}>
                  SKI-SERVICE KURIER
                </span>
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('services')} className={`text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
                Ski-Services
              </button>
              <button onClick={() => scrollToSection('service')} className={`text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
                {t('nav.service')}
              </button>
              <button onClick={() => scrollToSection('ablauf')} className={`text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
                {t('nav.process')}
              </button>
              <button onClick={() => scrollToSection('trust-bar')} className={`text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
                Unser Versprechen
              </button>
              <button onClick={() => scrollToSection('feedback-section')} className={`text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}>
                Feedback
              </button>
              <button
                onClick={toggleLanguage}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${isScrolled ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/20 text-white hover:bg-white/30'}`}
              >
                <i className="ri-global-line text-sm"></i>
                <span>{currentLanguage === 'de' ? 'DE' : 'EN'}</span>
              </button>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <div className="md:hidden flex flex-col items-end gap-1">
                <button onClick={openBooking} className="bg-[#2B8FBF] text-white px-2.5 xs:px-3 py-1 xs:py-1.5 rounded-full text-[10px] xs:text-xs font-semibold cursor-pointer whitespace-nowrap">
                  {t('nav.bookNow')}
                </button>
                <button
                  onClick={toggleLanguage}
                  className={`flex items-center gap-1 px-2 xs:px-2.5 py-0.5 xs:py-1 rounded-full text-[10px] xs:text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${isScrolled || mobileMenuOpen ? 'bg-gray-100 text-gray-700' : 'bg-white/20 text-white'}`}
                >
                  <i className="ri-global-line text-xs xs:text-sm"></i>
                  <span>{currentLanguage === 'de' ? 'DE' : 'EN'}</span>
                </button>
              </div>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden w-8 h-8 xs:w-9 xs:h-9 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 ${isScrolled || mobileMenuOpen ? 'text-gray-900' : 'text-white'}`}
                aria-label="Menü öffnen"
              >
                {mobileMenuOpen ? <i className="ri-close-line text-lg xs:text-xl"></i> : <i className="ri-menu-line text-lg xs:text-xl"></i>}
              </button>

              <button
                onClick={openBooking}
                className="hidden md:block px-5 py-2 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap bg-[#2B8FBF] text-white hover:bg-[#2480ad]"
              >
                {t('nav.bookNow')}
              </button>
            </div>
          </div>
        </div>

        <div className={`md:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-80' : 'max-h-0'} bg-white border-t ${mobileMenuOpen ? 'border-gray-100' : 'border-transparent'}`}>
          <div className="px-6">
            <button onClick={() => handleMobileNavClick('services')} className="w-full text-left py-3 font-semibold text-gray-800 border-b border-gray-100 cursor-pointer">Ski-Services</button>
            <button onClick={() => handleMobileNavClick('ablauf')} className="w-full text-left py-3 font-semibold text-gray-800 border-b border-gray-100 cursor-pointer">{t('nav.process')}</button>
            <button onClick={() => handleMobileNavClick('trust-bar')} className="w-full text-left py-3 font-semibold text-gray-800 border-b border-gray-100 cursor-pointer">Unser Versprechen</button>
            <button onClick={() => handleMobileNavClick('feedback-section')} className="w-full text-left py-3 font-semibold text-gray-800 border-b border-gray-100 cursor-pointer">Feedback</button>
            <button onClick={() => { setMobileMenuOpen(false); openBooking(); }} className="w-full text-left py-3 font-semibold text-gray-800 cursor-pointer">{t('nav.book')}</button>
          </div>
        </div>
      </nav>

      {/* Scroll Snap Container */}
      <div
        ref={scrollContainerRef}
        className="h-screen overflow-y-scroll bg-[#F0EDE6]"
        style={{ scrollSnapType: 'y mandatory', scrollBehavior: 'smooth' }}
        onScroll={(e) => {
          const scrollTop = (e.currentTarget as HTMLDivElement).scrollTop;
          setIsScrolled(scrollTop > 50);
          const index = Math.round(scrollTop / window.innerHeight);
          setCurrentSection(index);
        }}
      >
        {/* Section 1 – Hero (eager, above the fold) */}
        <div id="hero" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="min-h-screen">
          <Hero onBookClick={openBooking} />
        </div>

        {/* Section 2 – How It Works (eager, second visible section) */}
        <div id="how-it-works" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="min-h-screen overflow-y-auto pt-[72px] bg-[#F0EDE6]">
          <HowItWorks onBookClick={openBooking} />
        </div>

        {/* Section 3–6 – lazy loaded */}
        <div id="services" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="min-h-screen flex flex-col pt-[72px] bg-[#F8F6F0] overflow-y-auto">
          <Suspense fallback={<SectionSkeleton />}>
            <ServiceOverview onBookClick={openBookingWithService} />
          </Suspense>
        </div>
        <div id="trust-bar" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="min-h-screen overflow-y-auto pt-[72px] bg-[#F0EDE6]">
          <Suspense fallback={<SectionSkeleton />}>
            <TrustBar />
          </Suspense>
        </div>
        <div id="feedback-section" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="min-h-screen overflow-y-auto pt-[72px] bg-[#F0EDE6]">
          <Suspense fallback={<SectionSkeleton />}>
            <FeedbackSection />
          </Suspense>
        </div>
        <div id="footer-section" style={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="h-screen overflow-hidden pt-[72px] bg-[#F0EDE6]">
          <Suspense fallback={<SectionSkeleton />}>
            <Footer />
          </Suspense>
        </div>
      </div>

      {/* Navigation Arrows */}
      {currentSection > 0 && (
        <button
          onClick={() => scrollToIndex(currentSection - 1)}
          className="fixed z-40 w-10 h-10 flex items-center justify-center rounded-full bg-gray-800/70 backdrop-blur-sm border border-gray-600/50 text-white hover:bg-gray-800/90 transition-all cursor-pointer shadow-lg"
          aria-label="Vorheriger Abschnitt"
          style={{ top: '80px', left: '50%', transform: 'translateX(-50%)' }}
        >
          <i className="ri-arrow-up-s-line text-2xl"></i>
        </button>
      )}

      {currentSection < totalSections - 1 && (
        <button
          onClick={() => scrollToIndex(currentSection + 1)}
          className={`fixed z-40 w-10 h-10 flex items-center justify-center rounded-full bg-gray-800/70 backdrop-blur-sm border border-gray-600/50 text-white hover:bg-gray-800/90 transition-all cursor-pointer shadow-lg ${currentSection < 2 ? 'animate-bounce' : ''}`}
          aria-label="Nächster Abschnitt"
          style={{ bottom: '16px', left: '0', right: '0', marginLeft: 'auto', marginRight: 'auto', width: '40px' }}
        >
          <i className="ri-arrow-down-s-line text-2xl"></i>
        </button>
      )}

      {/* Dot Navigation */}
      <div className="fixed right-5 z-50 flex flex-col gap-3" style={{ top: '88px' }}>
        {sections.map((section, index) => (
          <button
            key={section}
            onClick={() => scrollToIndex(index)}
            aria-label={`Zu Abschnitt ${index + 1}`}
            className="cursor-pointer transition-all duration-300 flex items-center justify-center"
          >
            <span
              className={`block rounded-full transition-all duration-300 ${
                currentSection === index
                  ? 'w-3 h-3 bg-[#2B8FBF] shadow-md'
                  : 'w-2 h-2 bg-gray-700/70 hover:bg-gray-900 border border-gray-500/60'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Booking Modal – lazy loaded, only mounted when opened */}
      {showBooking && (
        <Suspense fallback={null}>
          <ContactBooking
            isOpen={showBooking}
            onClose={() => setShowBooking(false)}
            preselectedService={selectedService}
          />
        </Suspense>
      )}
    </div>
  );
}
